import type { ChangeEvent, MouseEvent, RefObject } from "react";
import { LuImage } from "react-icons/lu";
import { FaLocationCrosshairs } from "react-icons/fa6";
import { useTranslations } from "next-intl";

type Position = {
    x: number;
    y: number;
};

type Answer = {
    id: number;
    text: string;
    isCorrect: boolean;
    imageFileName: string;
    audioFileName: string;
};

type Question = {
    id: number;
    text: string;
    position: Position | null;
    imageFileName: string;
    audioFileName: string;
    answers: Answer[];
};

type SituationAsset = {
    id: number;
    position: Position;
    imageUrl: string | null;
};

const DEFAULT_POSITION: Position = {
    x: 0.5,
    y: 0.5,
};

function formatPositionValue(value: number) {
    return value.toFixed(3).replace(/\.?0+$/, "");
}

function getEffectivePosition(position: Position | null) {
    return position ?? DEFAULT_POSITION;
}

type PositionCanvasProps = {
    questions: Question[];
    selectedQuestionId: number | null;
    selectedQuestion: Question | null;
    positionImageUrl: string;
    positionAssets: SituationAsset[];
    selectedAsset: SituationAsset | null;
    isAddingPositionAsset: boolean;
    positionFrameRef: RefObject<HTMLDivElement | null>;
    positionImageInputRef: RefObject<HTMLInputElement | null>;
    positionAssetInputRef: RefObject<HTMLInputElement | null>;
    onPositionMouseDown: (event: MouseEvent<HTMLDivElement>) => void;
    onPositionMouseMove: (event: MouseEvent<HTMLDivElement>) => void;
    onPositionMouseUp: () => void;
    onPositionImageSelected: (event: ChangeEvent<HTMLInputElement>) => void;
    onPositionAssetFileSelected: (event: ChangeEvent<HTMLInputElement>) => void;
    onPositionAssetSelected: (assetId: number) => void;
    onOpenPositionImagePicker: () => void;
    onRemovePositionImage: () => void;
    onClickAddPositionAsset: () => void;
    onClickReplaceSelectedPositionAsset: () => void;
    onClickRemoveSelectedPositionAsset: () => void;
    onSelectQuestion: (questionId: number) => void;
};

export default function PositionCanvas({
    questions,
    selectedQuestionId,
    selectedQuestion,
    positionImageUrl,
    positionAssets,
    selectedAsset,
    isAddingPositionAsset,
    positionFrameRef,
    positionImageInputRef,
    positionAssetInputRef,
    onPositionMouseDown,
    onPositionMouseMove,
    onPositionMouseUp,
    onPositionImageSelected,
    onPositionAssetFileSelected,
    onPositionAssetSelected,
    onOpenPositionImagePicker,
    onRemovePositionImage,
    onClickAddPositionAsset,
    onClickReplaceSelectedPositionAsset,
    onClickRemoveSelectedPositionAsset,
    onSelectQuestion,
}: PositionCanvasProps) {
    const tSituation = useTranslations("situation");
    const canvasCursorClass = isAddingPositionAsset
        ? "cursor-copy"
        : selectedQuestionId
            ? "cursor-crosshair"
            : "cursor-not-allowed";

    return (
        <div className="mt-4">
            <div className="mb-3 grid grid-cols-1 gap-2 lg:grid-cols-[960px_340px] lg:justify-center lg:gap-3">
                <div className="overflow-x-auto">
                    <div className="mr-auto grid w-180 min-w-[760px] grid-cols-1 gap-2 md:grid-cols-2 lg:min-w-0">
                        <div className="flex items-center gap-2 rounded-lg bg-[#F5F8FC] px-3 py-2 text-sm text-[#5a5a5a]">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1e8ef7] text-xs font-bold text-white">1</span>
                            <span>{tSituation("positionStepSelectImage")}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-[#F5F8FC] px-3 py-2 text-sm text-[#5a5a5a]">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1e8ef7] text-xs font-bold text-white">2</span>
                            <span>{tSituation("positionStepClickCanvas")}</span>
                        </div>
                    </div>
                </div>
                <div />
            </div>

            <div className="grid grid-cols-1 gap-2 lg:grid-cols-[960px_340px] lg:justify-center lg:gap-3">
                <div className="overflow-x-auto">
                    <div
                        ref={positionFrameRef}
                        className={`relative mr-auto h-67.5 w-240 min-w-[760px] select-none overflow-hidden rounded-lg border border-[#bfc2c8] bg-white lg:min-w-0 ${canvasCursorClass}`}
                        onMouseDown={onPositionMouseDown}
                        onMouseMove={onPositionMouseMove}
                        onMouseUp={onPositionMouseUp}
                        onMouseLeave={onPositionMouseUp}
                    >
                        {positionImageUrl ? (
                            <img src={positionImageUrl} alt={tSituation("positionBackgroundAlt")} className="absolute inset-0 h-full w-full object-fill" />
                        ) : null}

                        {!positionImageUrl ? (
                            <div className="absolute inset-0 flex items-center justify-center text-lg font-semibold text-[#d7597f]">
                                {tSituation("positionImageSize")}
                            </div>
                        ) : null}

                        {positionAssets.map((asset) => {
                            const isSelectedAsset = selectedAsset?.id === asset.id;

                            return (
                                <button
                                    key={`asset-${asset.id}`}
                                    type="button"
                                    onMouseDown={(event) => {
                                        event.stopPropagation();
                                        onPositionAssetSelected(asset.id);
                                    }}
                                    className={`absolute -translate-x-1/2 -translate-y-1/2 overflow-hidden border ${isSelectedAsset ? "border-[#1f89ff]" : "border-none"}`}
                                    style={{
                                        left: `${asset.position.x * 100}%`,
                                        top: `${asset.position.y * 100}%`,
                                    }}
                                    title={tSituation("addAsset")}
                                >
                                    {asset.imageUrl ? (
                                        <img
                                            src={asset.imageUrl}
                                            alt={tSituation("addAsset")}
                                            className="max-h-16 max-w-24 object-contain"
                                        />
                                    ) : null}
                                </button>
                            );
                        })}

                        {questions.map((question, index) => {
                            const position = getEffectivePosition(question.position);
                            const isActive = question.id === selectedQuestionId;

                            return (
                                <div
                                    key={`marker-${question.id}`}
                                    className="absolute -translate-x-1/2 -translate-y-1/2"
                                    style={{
                                        left: `${position.x * 100}%`,
                                        top: `${position.y * 100}%`,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onMouseDown={(event) => {
                                            event.stopPropagation();
                                            onSelectQuestion(question.id);
                                        }}
                                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white shadow-[0_1px_0_rgba(0,0,0,0.2)] ${isActive ? "bg-[#df4848]" : "bg-[#20a8e0]"}`}
                                        title={`${tSituation("questionNumber")} ${index + 1}`}
                                    >
                                        {index + 1}
                                    </button>
                                </div>
                            );
                        })}

                        <div className="absolute left-3 top-3 flex items-center gap-2 rounded border border-[#d2d2d2] bg-white px-2 py-1 text-xs shadow-sm">
                            <FaLocationCrosshairs className="text-[#df4848]" size={13} />
                            <span className="font-semibold text-[#555]">{tSituation("positionLabel")}</span>
                            {(() => {
                                const displayPosition = selectedQuestion
                                    ? getEffectivePosition(selectedQuestion.position)
                                    : selectedAsset
                                        ? getEffectivePosition(selectedAsset.position)
                                        : null;

                                return (
                                    <>
                                        <span className="rounded border border-[#d8d8d8] bg-[#f7f7f7] px-1.5 py-0.5 text-[#333]">
                                            X: {displayPosition ? formatPositionValue(displayPosition.x) : "-"}
                                        </span>
                                        <span className="rounded border border-[#d8d8d8] bg-[#f7f7f7] px-1.5 py-0.5 text-[#333]">
                                            Y: {displayPosition ? formatPositionValue(displayPosition.y) : "-"}
                                        </span>
                                    </>
                                );
                            })()}
                        </div>

                        <div className="absolute right-3 top-3 flex items-center gap-2">
                            <input
                                ref={positionImageInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                className="hidden"
                                onChange={onPositionImageSelected}
                            />
                            <input
                                ref={positionAssetInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                className="hidden"
                                onChange={onPositionAssetFileSelected}
                            />

                            <button
                                type="button"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={onOpenPositionImagePicker}
                                className="flex items-center gap-1 rounded border border-[#c8c8c8] bg-white px-2 py-1 text-xs text-[#4c4c4c] shadow-sm hover:bg-[#f4f4f4]"
                            >
                                <LuImage size={14} />
                                {positionImageUrl ? tSituation("changeImage") : tSituation("uploadImage")}
                            </button>

                            {positionImageUrl ? (
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={onRemovePositionImage}
                                    className="rounded border border-[#efbaba] bg-white px-2 py-1 text-xs text-[#c85050] shadow-sm hover:bg-[#fff2f2]"
                                >
                                    {tSituation("deleteImage")}
                                </button>
                            ) : null}

                            <button
                                type="button"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={onClickAddPositionAsset}
                                className={`rounded border px-2 py-1 text-xs shadow-sm ${isAddingPositionAsset
                                    ? "border-[#0b9e66] bg-[#e9fff5] text-[#0b9e66]"
                                    : "border-[#b8d8be] bg-white text-[#2f7a45] hover:bg-[#f1fff5]"
                                    }`}
                            >
                                {tSituation("addAsset")}
                            </button>

                            {selectedAsset ? (
                                <>
                                    <button
                                        type="button"
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={onClickReplaceSelectedPositionAsset}
                                        className="rounded border border-[#c8c8c8] bg-white px-2 py-1 text-xs text-[#4c4c4c] shadow-sm hover:bg-[#f4f4f4]"
                                    >
                                        {tSituation("changeAsset")}
                                    </button>
                                    <button
                                        type="button"
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={onClickRemoveSelectedPositionAsset}
                                        className="rounded border border-[#efbaba] bg-white px-2 py-1 text-xs text-[#c85050] shadow-sm hover:bg-[#fff2f2]"
                                    >
                                        {tSituation("deleteAsset")}
                                    </button>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="flex h-67.5 flex-col rounded-lg border border-[#d9dce3] bg-white">
                    <div className="flex shrink-0 items-center justify-between border-b border-[#dde1e8] px-3 py-2 text-sm font-semibold text-[#4d4f56]">
                        <span>{tSituation("questionPointList")}</span>
                        <span>({questions.length})</span>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                        {questions.map((question, index) => {
                            const isActive = question.id === selectedQuestionId;
                            const questionText = question.text.trim() || "-";

                            return (
                                <button
                                    key={`point-list-${question.id}`}
                                    type="button"
                                    onClick={() => onSelectQuestion(question.id)}
                                    className={`mb-1 flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-left transition-colors cursor-pointer ${isActive
                                        ? "border-[#f5a2b2] bg-[#ffeef2]"
                                        : "border-[#d7dce5] bg-white hover:bg-[#f8faff]"
                                        }`}
                                >
                                    <div className="min-w-0">
                                        <div className={`text-sm font-semibold ${isActive ? "text-[#e54668]" : "text-[#1f86e2]"}`}>
                                            {tSituation("pointLabel")} {index + 1}
                                        </div>
                                        <div className="truncate text-xs text-[#666]">{questionText}</div>
                                    </div>
                                    {/* <span className="ml-2 shrink-0 text-[#555]">↕</span> */}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
